import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Provides a global, lightweight pollable view of the user's unread leads.
// Polls every 60s + manual `refresh()` after critical events (e.g., opening a client).
const LeadsContext = createContext({
  count: 0,
  items: [],
  refresh: () => Promise.resolve(),
});

export function LeadsProvider({ children }) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/clients/leads/unread");
      setCount(data?.count || 0);
      setItems(data?.items || []);
    } catch {
      // silent — don't toast for a polling background call
    }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) {
      setCount(0);
      setItems([]);
      return;
    }
    refresh();
    timerRef.current = setInterval(refresh, 60000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, refresh]);

  return (
    <LeadsContext.Provider value={{ count, items, refresh }}>
      {children}
    </LeadsContext.Provider>
  );
}

export function useLeads() {
  return useContext(LeadsContext);
}
